import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Avatar,
  Card,
  CardContent,
  Grid,
  Stack,
  Divider,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  Edit,
  Save,
  Cancel,
  Logout,
  Favorite,
  Category,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { url } from '../constants';

const Profile = () => {
  const [user, setUser] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [logoutDialog, setLogoutDialog] = useState(false);

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchUserProfile();
    fetchCategories();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${url}/users/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch profile');
      }

      const data = await response.json();
      console.log('User profile:', data);
      setUser(data);
      setSelectedCategories(data.preferences || []);
    } catch (error) {
      console.error('Error fetching profile:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load profile',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${url}/users/get_categories`);
      if (!response.ok) {
        throw new Error('Failed to fetch categories');
      }
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  const handleSavePreferences = async () => {
    if (selectedCategories.length < 3) {
      setSnackbar({
        open: true,
        message: 'Please select at least 3 categories',
        severity: 'warning'
      });
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${url}/users/update-preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ preferences: selectedCategories })
      });

      if (!response.ok) {
        throw new Error('Failed to update preferences');
      }

      setSnackbar({
        open: true,
        message: 'Preferences updated successfully!',
        severity: 'success'
      });
      setEditMode(false);
      fetchUserProfile();
    } catch (error) {
      console.error('Error updating preferences:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update preferences',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('userFavorites');
    navigate('/login');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress sx={{ color: '#1a7f3a' }} />
      </Box>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Alert severity="error">Failed to load profile</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: '#F7F7F7', minHeight: '100vh', py: isMobile ? 2 : 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700} sx={{ color: '#0F1111', mb: 1 }}>
            My Profile
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your account and preferences
          </Typography>
        </Box>

        {/* Profile & Stats Section - Centered */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: isMobile ? 2 : 4 }}>
          <Box sx={{ maxWidth: isMobile ? '100%' : '700px', width: '100%' }}>
            {/* Profile Card */}
            <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', mb: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Avatar
                  sx={{
                    width: isMobile ? 80 : 100,
                    height: isMobile ? 80 : 100,
                    bgcolor: '#1a7f3a',
                    fontSize: isMobile ? '2rem' : '2.5rem',
                    margin: '0 auto',
                    mb: 2,
                  }}
                >
                  {user.full_name?.charAt(0).toUpperCase() || 'U'}
                </Avatar>

                <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={600} sx={{ mb: 0.5 }}>
                  {user.full_name || 'User'}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {user.email}
                </Typography>

                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<Logout />}
                  onClick={() => setLogoutDialog(true)}
                  sx={{
                    borderColor: '#FF6B6B',
                    color: '#FF6B6B',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#FF6B6B',
                      bgcolor: 'rgba(255, 107, 107, 0.04)',
                    },
                  }}
                >
                  Logout
                </Button>
              </CardContent>
            </Card>

            {/* Stats Cards - Side by Side */}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Card 
                  sx={{ 
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)',
                    color: 'white',
                  }}
                >
                  <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                    <Stack direction="column" alignItems="center" spacing={1}>
                      <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: isMobile ? 40 : 50, height: isMobile ? 40 : 50 }}>
                        <Favorite sx={{ fontSize: isMobile ? 22 : 28 }} />
                      </Avatar>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ opacity: 0.9, fontSize: isMobile ? '0.7rem' : '0.875rem' }}>
                          Saved Deals
                        </Typography>
                        <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700}>
                          {user.favorite_deals_count || 0}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={6}>
                <Card 
                  sx={{ 
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    background: 'linear-gradient(135deg, #1a7f3a 0%, #2ecc71 100%)',
                    color: 'white',
                  }}
                >
                  <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                    <Stack direction="column" alignItems="center" spacing={1}>
                      <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: isMobile ? 40 : 50, height: isMobile ? 40 : 50 }}>
                        <Category sx={{ fontSize: isMobile ? 22 : 28 }} />
                      </Avatar>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ opacity: 0.9, fontSize: isMobile ? '0.7rem' : '0.875rem' }}>
                          Interests
                        </Typography>
                        <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700}>
                          {user.preference_count || 0}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* Basic Information */}
        <Card sx={{ mb: isMobile ? 2 : 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
              Basic Information
            </Typography>

            <Stack spacing={2.5}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Person sx={{ color: '#1a7f3a' }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Full Name
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {user.full_name || 'Not provided'}
                  </Typography>
                </Box>
              </Stack>

              <Divider />

              <Stack direction="row" spacing={2} alignItems="center">
                <Email sx={{ color: '#1a7f3a' }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Email Address
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {user.email || 'Not provided'}
                  </Typography>
                </Box>
              </Stack>

              <Divider />

              <Stack direction="row" spacing={2} alignItems="center">
                <Phone sx={{ color: '#1a7f3a' }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Phone Number
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {user.phone || 'Not provided'}
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Category Preferences */}
        <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
                  Category Preferences
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Select categories you're interested in to get personalized deals
                </Typography>
              </Box>
              {!editMode && (
                <IconButton
                  onClick={() => setEditMode(true)}
                  sx={{
                    bgcolor: '#E8F5E9',
                    color: '#1a7f3a',
                    '&:hover': {
                      bgcolor: '#C8E6C9',
                    },
                  }}
                >
                  <Edit />
                </IconButton>
              )}
            </Stack>

            {editMode && (
              <Alert severity="info" sx={{ mb: 3 }}>
                Select the categories you're interested in. You must select at least 3 categories.
              </Alert>
            )}

            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Selected: <strong>{selectedCategories.length}</strong> categories
            </Typography>

            {/* Categories with Colors - Less Bulky on Mobile */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 1 : 2, mb: editMode ? 3 : 0 }}>
              {categories.map((category) => {
                const isSelected = selectedCategories.includes(category.cate_id);
                const categoryColor = category.cate_color || '#1a7f3a';
                
                return (
                  <Button
                    key={category.cate_id}
                    onClick={editMode ? () => handleCategoryToggle(category.cate_id) : undefined}
                    variant={isSelected ? 'contained' : 'outlined'}
                    disabled={!editMode}
                    sx={{
                      borderRadius: '20px',
                      px: isMobile ? 2 : 3,
                      py: isMobile ? 0.8 : 1.5,
                      bgcolor: isSelected ? categoryColor : 'transparent',
                      borderColor: isSelected ? categoryColor : '#ddd',
                      borderWidth: 2,
                      color: isSelected ? 'white' : 'text.primary',
                      fontWeight: 600,
                      fontSize: isMobile ? '0.75rem' : '0.9rem',
                      cursor: editMode ? 'pointer' : 'default',
                      transition: 'all 0.3s ease',
                      boxShadow: isSelected ? `0 4px 12px ${categoryColor}40` : 'none',
                      '&:hover': editMode ? {
                        bgcolor: isSelected ? categoryColor : 'action.hover',
                        transform: 'scale(1.05)',
                        boxShadow: isSelected ? `0 6px 16px ${categoryColor}60` : 'none',
                      } : {},
                      '&:disabled': {
                        bgcolor: isSelected ? categoryColor : 'transparent',
                        borderColor: isSelected ? categoryColor : '#ddd',
                        color: isSelected ? 'white' : 'text.secondary',
                        opacity: editMode ? 0.6 : 1,
                      },
                    }}
                  >
                    {category.cate_name}
                  </Button>
                );
              })}
            </Box>

            {/* Action Buttons - Vertical on Mobile */}
            {editMode && (
              <Stack 
                direction={isMobile ? 'column' : 'row'} 
                spacing={2} 
                justifyContent="flex-end"
              >
                <Button
                  fullWidth={isMobile}
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={() => {
                    setEditMode(false);
                    setSelectedCategories(user.preferences || []);
                  }}
                  sx={{
                    borderColor: '#D5D9D9',
                    color: '#4B5563',
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  fullWidth={isMobile}
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <Save />}
                  onClick={handleSavePreferences}
                  disabled={saving || selectedCategories.length < 3}
                  sx={{
                    bgcolor: '#1a7f3a',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: '#0f5328',
                    },
                  }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Stack>
            )}
          </CardContent>
        </Card>
      </Container>

      {/* Logout Confirmation Dialog */}
      <Dialog open={logoutDialog} onClose={() => setLogoutDialog(false)}>
        <DialogTitle>Confirm Logout</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to logout?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutDialog(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleLogout}
            variant="contained"
            sx={{
              bgcolor: '#FF6B6B',
              textTransform: 'none',
              '&:hover': {
                bgcolor: '#FF5252',
              },
            }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Profile;
